import { Check, Loader2, Square } from 'lucide-react';
import { type ReactNode, useEffect, useId, useState } from 'react';

import { Button } from '@apps/web/components/ui/button';
import { Checkbox } from '@apps/web/components/ui/checkbox';
import { Input } from '@apps/web/components/ui/input';
import { cn } from '@apps/web/lib/utils';

export type MonitoringConfigPayload = {
    readonly conditions: Array<string>;
};

type MonitoringDisplayConfig = MonitoringConfigPayload;

export type MonitoringActivityControls = {
    readonly isStopping: boolean;
    readonly isConfiguring: boolean;
    readonly onStop: () => void;
    // Resolves true when the update request was accepted, so the card can
    // optimistically treat the submitted conditions as saved.
    readonly onConfigure: (
        payload: MonitoringConfigPayload,
    ) => Promise<boolean>;
};

export type MonitoringStreamRunNode = {
    readonly kind: string;
    readonly toolName: string | null;
    readonly arguments: Record<string, unknown> | Array<unknown> | null;
    readonly result: unknown;
};

export type MonitoringStreamRunState = {
    readonly orderedNodeIds: Array<string>;
    readonly nodesById: Record<string, MonitoringStreamRunNode | undefined>;
};

type MonitoringConditionFormState = {
    readonly conditions: Array<MonitoringConditionRow>;
    readonly custom: boolean;
    readonly customText: string;
};

type MonitoringConditionRow = {
    readonly condition: string;
    readonly checked: boolean;
};

const CUSTOM_MONITORING_PLACEHOLDERS = [
    'Add caregivers when no yes after 30 minutes',
    'Add caregivers when a caregiver declines a shift',
    'Pause outreach when a caregiver responds available',
    'Notify me when no more actions can be taken',
    'Notify me when someone accepts',
];
const CUSTOM_MONITORING_PLACEHOLDER_TRANSITION_MS = 250;

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function getStringListValue(
    value: Record<string, unknown> | null | undefined,
    key: string,
): Array<string> | undefined {
    const rawValue = value?.[key];
    if (!Array.isArray(rawValue)) {
        return undefined;
    }
    return rawValue.flatMap((item) => {
        if (typeof item !== 'string') {
            return [];
        }
        const trimmed = item.trim();
        return trimmed ? [trimmed] : [];
    });
}

function getMonitoringConfigFromTools(
    tools: ReadonlyArray<MonitoringStreamRunNode>,
    monitoringScheduleMetadata?: Record<string, unknown> | null,
): MonitoringDisplayConfig {
    let latestMonitorTool: MonitoringStreamRunNode | null = null;
    for (const tool of tools) {
        if (tool.toolName === 'monitor_outreach') {
            latestMonitorTool = tool;
        }
    }

    const args = isRecord(latestMonitorTool?.arguments)
        ? latestMonitorTool.arguments
        : null;
    const output = isRecord(latestMonitorTool?.result)
        ? latestMonitorTool.result
        : null;
    const metadataConditions = getStringListValue(
        monitoringScheduleMetadata,
        'conditions',
    );
    const argsConditions = getStringListValue(args, 'conditions');
    const outputConditions = getStringListValue(output, 'conditions');
    return {
        conditions:
            metadataConditions ?? argsConditions ?? outputConditions ?? [],
    };
}

function createMonitoringConditionFormState(
    conditions: Array<string>,
): MonitoringConditionFormState {
    // Every stored condition renders as its own checked row, whatever its
    // phrasing — the card never interprets condition text.
    return {
        conditions: conditions.map((condition) => ({
            condition,
            checked: true,
        })),
        custom: false,
        customText: '',
    };
}

function buildMonitoringConditions(
    state: MonitoringConditionFormState,
): Array<string> {
    const conditions = state.conditions.flatMap((conditionRow) =>
        conditionRow.checked ? [conditionRow.condition] : [],
    );
    if (state.custom) {
        const trimmedCustomText = state.customText.trim();
        if (trimmedCustomText.length > 0) {
            conditions.push(trimmedCustomText);
        }
    }
    return conditions;
}

function monitoringConditionsKey(conditions: Array<string>): string {
    return JSON.stringify([...conditions].sort());
}

function monitoringConditionsEqual(
    left: Array<string>,
    right: Array<string>,
): boolean {
    return monitoringConditionsKey(left) === monitoringConditionsKey(right);
}

export function getMonitoringToolNodesFromRun(
    run: MonitoringStreamRunState,
): Array<MonitoringStreamRunNode> {
    return run.orderedNodeIds
        .map((nodeId) => run.nodesById[nodeId])
        .filter(
            (node): node is MonitoringStreamRunNode =>
                node !== undefined &&
                node.kind === 'tool' &&
                node.toolName === 'monitor_outreach',
        );
}

function MonitoringCheckboxRow({
    checked,
    checkboxLabel,
    onCheckedChange,
    children,
}: {
    readonly checked: boolean;
    readonly checkboxLabel: string;
    readonly onCheckedChange: (checked: boolean) => void;
    readonly children: ReactNode;
}) {
    const checkboxId = useId();
    return (
        <div className="text-muted-foreground flex min-h-8 flex-wrap items-center gap-2 text-xs">
            <Checkbox
                id={checkboxId}
                checked={checked}
                aria-label={checkboxLabel}
                className="size-3.5 rounded-[3px] [&_svg]:size-3"
                onCheckedChange={(value) => onCheckedChange(value === true)}
            />
            {children}
        </div>
    );
}

export function MonitoringConfigurationCard({
    tools,
    controls,
    monitoringScheduleMetadata,
    className,
}: {
    readonly tools: ReadonlyArray<MonitoringStreamRunNode>;
    readonly controls: MonitoringActivityControls;
    readonly monitoringScheduleMetadata?: Record<string, unknown> | null;
    readonly className?: string;
}) {
    // Depend on primitive config values rather than the tools array or a
    // freshly-allocated config object: the parent re-renders frequently
    // during chat streaming, which produces a new tools array each render.
    // Object-ref deps would reset local state every render and clobber
    // the user's in-progress edits.
    const serverConfig = getMonitoringConfigFromTools(
        tools,
        monitoringScheduleMetadata,
    );
    const serverConditionsStateKey = JSON.stringify(serverConfig.conditions);
    const [conditionState, setConditionState] =
        useState<MonitoringConditionFormState>(() =>
            createMonitoringConditionFormState(serverConfig.conditions),
        );
    const [customPlaceholderIndex, setCustomPlaceholderIndex] = useState(0);
    const [customPlaceholderFading, setCustomPlaceholderFading] =
        useState(false);
    // The server only reflects new conditions after the agent processes the
    // update poke, which can take minutes. Track the last successfully
    // submitted conditions so the Update button clears immediately instead
    // of inviting duplicate submissions while the agent catches up.
    const [submittedConditionsKey, setSubmittedConditionsKey] = useState<
        string | null
    >(null);
    const normalizedConditions = buildMonitoringConditions(conditionState);
    const configChanged =
        !monitoringConditionsEqual(
            normalizedConditions,
            serverConfig.conditions,
        ) &&
        monitoringConditionsKey(normalizedConditions) !==
            submittedConditionsKey;

    useEffect(() => {
        const parsedConditions = JSON.parse(
            serverConditionsStateKey,
        ) as Array<string>;
        setConditionState(createMonitoringConditionFormState(parsedConditions));
        setSubmittedConditionsKey(null);
    }, [serverConditionsStateKey]);

    useEffect(() => {
        let timeoutId: number | undefined;
        const intervalId = window.setInterval(() => {
            setCustomPlaceholderFading(true);
            timeoutId = window.setTimeout(() => {
                setCustomPlaceholderIndex(
                    (currentIndex) =>
                        (currentIndex + 1) %
                        CUSTOM_MONITORING_PLACEHOLDERS.length,
                );
                setCustomPlaceholderFading(false);
            }, CUSTOM_MONITORING_PLACEHOLDER_TRANSITION_MS);
        }, 3_000);
        return () => {
            window.clearInterval(intervalId);
            if (timeoutId !== undefined) {
                window.clearTimeout(timeoutId);
            }
        };
    }, []);

    function updateConditionState(
        update: Partial<MonitoringConditionFormState>,
    ): void {
        setConditionState((current) => ({ ...current, ...update }));
    }

    function setConditionChecked(condition: string, checked: boolean): void {
        setConditionState((current) => ({
            ...current,
            conditions: current.conditions.map((conditionRow) =>
                conditionRow.condition === condition
                    ? { ...conditionRow, checked }
                    : conditionRow,
            ),
        }));
    }

    return (
        <div
            className={cn(
                'border-border/50 bg-background max-w-md rounded-md border p-3 text-left',
                className,
            )}
        >
            <div className="text-foreground flex flex-wrap items-center gap-1.5 text-sm">
                <Loader2
                    className="text-primary h-3.5 w-3.5 flex-shrink-0 animate-spin"
                    aria-hidden="true"
                />
                <span>Phoebe is monitoring this outreach</span>
            </div>
            <div className="mt-3 grid gap-2">
                {conditionState.conditions.map((conditionRow) => (
                    <MonitoringCheckboxRow
                        key={conditionRow.condition}
                        checked={conditionRow.checked}
                        checkboxLabel={conditionRow.condition}
                        onCheckedChange={(checked) =>
                            setConditionChecked(conditionRow.condition, checked)
                        }
                    >
                        <span>{conditionRow.condition}</span>
                    </MonitoringCheckboxRow>
                ))}
                <MonitoringCheckboxRow
                    checked={conditionState.custom}
                    checkboxLabel="Enable custom monitoring condition"
                    onCheckedChange={(checked) =>
                        updateConditionState({ custom: checked })
                    }
                >
                    <div className="relative min-w-56 flex-1">
                        <Input
                            aria-label="Custom monitoring condition"
                            className="text-muted-foreground placeholder:text-muted-foreground h-7 px-2 text-xs placeholder:text-transparent md:text-xs placeholder:md:text-xs"
                            value={conditionState.customText}
                            placeholder=" "
                            onFocus={() =>
                                updateConditionState({ custom: true })
                            }
                            onChange={(event) =>
                                updateConditionState({
                                    custom: true,
                                    customText: event.target.value,
                                })
                            }
                        />
                        {conditionState.customText.length === 0 ? (
                            <span
                                className={cn(
                                    'text-muted-foreground pointer-events-none absolute top-1/2 left-2 -translate-y-1/2 text-xs whitespace-nowrap transition-opacity duration-[250ms] md:text-xs',
                                    customPlaceholderFading
                                        ? 'opacity-0'
                                        : 'opacity-100',
                                )}
                            >
                                {
                                    CUSTOM_MONITORING_PLACEHOLDERS[
                                        customPlaceholderIndex
                                    ]
                                }
                            </span>
                        ) : null}
                    </div>
                </MonitoringCheckboxRow>
            </div>
            <div className="mt-3 flex flex-wrap justify-end gap-2">
                {configChanged ? (
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="bg-primary text-primary-foreground hover:bg-primary/90 ml-auto h-6 px-1.5 text-[11px] disabled:opacity-50"
                        disabled={controls.isConfiguring}
                        onClick={() => {
                            void controls
                                .onConfigure({
                                    conditions: normalizedConditions,
                                })
                                .then((saved) => {
                                    if (saved) {
                                        setSubmittedConditionsKey(
                                            monitoringConditionsKey(
                                                normalizedConditions,
                                            ),
                                        );
                                    }
                                });
                        }}
                    >
                        {controls.isConfiguring ? (
                            <Loader2 className="mr-1 h-2.5 w-2.5 animate-spin" />
                        ) : (
                            <Check className="mr-1 h-2.5 w-2.5" />
                        )}
                        Update
                    </Button>
                ) : null}
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="bg-primary text-primary-foreground hover:bg-primary/90 h-6 px-1.5 text-[11px] disabled:opacity-50"
                    disabled={controls.isStopping}
                    onClick={controls.onStop}
                >
                    {controls.isStopping ? (
                        <Loader2 className="mr-1 h-2.5 w-2.5 animate-spin" />
                    ) : (
                        <Square className="mr-1 h-2.5 w-2.5" />
                    )}
                    Stop monitoring
                </Button>
            </div>
        </div>
    );
}
